import { useState } from 'react';
import { Mail, Phone } from 'lucide-react';
import { errorMessage, operatorApi, useFetch } from '../../services/operatorApi';
import { Badge, Button, Card, Empty, ErrorBox, PageHeader, Spinner, ago, dateTime } from '../../components/ui';

interface AccessRequest {
  id: string;
  name: string;
  email: string;
  company: string | null;
  phone: string | null;
  volume: string | null;
  message: string | null;
  status: 'new' | 'contacted' | 'converted' | 'rejected';
  created_at: string;
  handled_at: string | null;
}

const STATUS: Record<AccessRequest['status'], [string, 'amber' | 'blue' | 'green' | 'slate']> = {
  new: ['Nouvelle', 'amber'],
  contacted: ['Contactée', 'blue'],
  converted: ['Client créé', 'green'],
  rejected: ['Refusée', 'slate'],
};

const VOLUME: Record<string, string> = {
  lt100: 'moins de 100 / mois',
  '100-1000': '100 à 1 000 / mois',
  '1000-5000': '1 000 à 5 000 / mois',
  gt5000: 'plus de 5 000 / mois',
};

export default function RequestsPage() {
  const { data, error, loading, reload } = useFetch(async () => (await operatorApi.get<{ requests: AccessRequest[]; pending: number }>('/access-requests')).data);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function setStatus(id: string, status: AccessRequest['status']) {
    setBusy(id);
    setFailure(null);
    try {
      await operatorApi.patch(`/access-requests/${id}`, { status });
      await reload();
    } catch (e) {
      setFailure(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <PageHeader title="Demandes d'accès" subtitle="Les prospects qui ont rempli le formulaire de la vitrine. Pour en faire un client, créez-le dans « Clients » puis marquez la demande." />
      {failure && <div className="mb-4"><ErrorBox message={failure} /></div>}
      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorBox message={error} onRetry={reload} />
      ) : !data || data.requests.length === 0 ? (
        <Card><Empty>Aucune demande pour le moment.</Empty></Card>
      ) : (
        <div className="space-y-4">
          {data.requests.map((request) => (
            <Card key={request.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{request.name}</span>
                    <Badge tone={STATUS[request.status][1]}>{STATUS[request.status][0]}</Badge>
                  </div>
                  <div className="text-sm text-slate-500">{request.company || 'Sans société'} · {VOLUME[request.volume ?? ''] ?? 'volume non précisé'}</div>
                </div>
                <div className="text-right text-xs text-slate-500" title={dateTime(request.created_at)}>Reçue {ago(request.created_at)}</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <a className="flex items-center gap-1.5 text-indigo-600 hover:underline" href={`mailto:${request.email}`}><Mail className="h-4 w-4" />{request.email}</a>
                {request.phone && <a className="flex items-center gap-1.5 text-slate-700" href={`tel:${request.phone}`}><Phone className="h-4 w-4" />{request.phone}</a>}
              </div>
              {request.message && <p className="mt-3 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{request.message}</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                {request.status !== 'contacted' && <Button small variant="secondary" disabled={busy === request.id} onClick={() => setStatus(request.id, 'contacted')}>Contactée</Button>}
                {request.status !== 'converted' && <Button small variant="secondary" disabled={busy === request.id} onClick={() => setStatus(request.id, 'converted')}>Client créé</Button>}
                {request.status !== 'rejected' && <Button small variant="secondary" disabled={busy === request.id} onClick={() => setStatus(request.id, 'rejected')}>Refuser</Button>}
                {request.status !== 'new' && <Button small variant="secondary" disabled={busy === request.id} onClick={() => setStatus(request.id, 'new')}>Remettre à traiter</Button>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
